import './i18n/autoTranslate';


import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Crown,
  Gauge,
  Layers,
  Lock,
  MessageSquare,
  Mic,
  ShieldCheck,
  Sparkles,
  Target,
  UserRound,
  Users,
  X,
  Send,
  SkipForward,
  BarChart3,
  CheckCircle2,
  AlertTriangle,
  Brain,
  Stars,
  Loader2,
} from "lucide-react";

import { useLocation } from "react-router-dom";

// shadcn/ui (make sure you have these in your project root)
import { Button } from "../components/ui/shadcn/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/shadcn/card";
import { Badge } from "../components/ui/shadcn/badge";
import { Input } from "../components/ui/shadcn/input";
import { Label } from "../components/ui/shadcn/label";
import { Textarea } from "../components/ui/shadcn/textarea";
import { Progress } from "../components/ui/shadcn/progress";
import { Separator } from "../components/ui/shadcn/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/shadcn/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/shadcn/accordion";

import Confetti from 'react-confetti'

// recharts for radar chart
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
} from "recharts";
import {  generateQuestions, analyzeTextWithBackend, analyzeAnswersWithAI, submitInterviewResultsPrepared } from "../services/interview/backendService";

import { loadConfiguration } from "../services/interview/configService";
import { createInterviewSession ,checkIfCanUseInterview} from "../services/interviewBackend";

/**
 * ChatInterview.jsx
 * Premium 3-step configuration wizard + structured interview chat + subtle real-time analysis sidebar + results dashboard.
 * - Duration fixed: 15 minutes
 * - Questions fixed: 8
 *
 * Replace MOCK_* and simulate calls with your real backend endpoints.
 */


const INTERVIEW_DURATION_SEC = 15 * 60;
const TOTAL_QUESTIONS = 8;

const COMPANY_PRESETS = [
  { id: "google", name: "Google" },
  { id: "meta", name: "Meta" },
  { id: "amazon", name: "Amazon" },
  { id: "netflix", name: "Netflix" },
  { id: "microsoft", name: "Microsoft" },
  { id: "apple", name: "Apple" },
];

const INTERVIEW_TYPES = [
  {
    id: "behavioral",
    title: "Behavioral",
    desc: "Leadership, collaboration, conflict, ownership.",
    tone: "Professional • Warm",
    icon: UserRound,
  },
  {
    id: "technical",
    title: "Technical",
    desc: "Depth, reasoning, tradeoffs, fundamentals.",
    tone: "Focused • Precise",
    icon: Layers,
  },
  {
    id: "stress",
    title: "Stress Interview",
    desc: "Pressure, ambiguity, pushback, time constraint.",
    tone: "Strict • Pressure-based",
    icon: Gauge,
  },
  {
    id: "panel",
    title: "Panel Interview",
    desc: "Multiple interviewers, varied perspectives.",
    tone: "Multi-persona • Realistic",
    icon: Users,
  },
];

const PANEL_PERSONAS = [
  { id: "hm", name: "Hiring Manager", role: "Vision & ownership", accent: "from-violet-500/25 to-fuchsia-500/10" },
  { id: "hr", name: "HR Partner", role: "Culture & clarity", accent: "from-emerald-500/20 to-cyan-500/10" },
  { id: "tl", name: "Tech Lead", role: "Depth & tradeoffs", accent: "from-indigo-500/25 to-sky-500/10" },
];

const FALLBACK_QUESTIONS = [
  "Tell me about yourself and your background.",
  "Why are you interested in this role and company style?",
  "Describe a challenging project and how you handled it.",
  "Tell me about a time you dealt with conflict in a team.",
  "How do you prioritize when everything is urgent?",
  "Explain a complex concept to a non-technical stakeholder.",
  "What’s a failure you learned from and what changed afterward?",
  "Where do you see yourself professionally in 2–3 years?",
];


/* ----------------------------- TechApp-style UI Primitives ----------------------------- */

const AppBackground = ({ children }) => (
  <div className="min-h-screen w-full bg-[#07090c] text-white relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-[80px]" />
      <div className="absolute -bottom-28 -right-24 h-[560px] w-[560px] rounded-full bg-sky-400/10 blur-[90px]" />
      <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_65%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
      <div className="absolute inset-0 opacity-35 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px]" />
      <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:92px_92px]" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/70" />
    </div>
    <div className="relative">{children}</div>
  </div>
);

const Glass = ({ children, className = "" }) => (
  <div
    className={[
      "rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl",
      "shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]",
      className,
    ].join(" ")}
  >
    {children}
  </div>
);

const PrimaryButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={[
      "inline-flex items-center justify-center px-5 py-2.5 rounded-2xl font-semibold text-sm",
      "bg-white text-black hover:bg-white/90",
      "shadow-[0_12px_30px_-18px_rgba(255,255,255,0.50)]",
      "transition disabled:opacity-50 disabled:hover:bg-white",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const GhostButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={[
      "inline-flex items-center justify-center px-5 py-2.5 rounded-2xl font-semibold text-sm",
      "border border-white/12 bg-white/5 hover:bg-white/8",
      "text-white/85 transition disabled:opacity-50",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const Pill = ({ children, tone = "neutral" }) => {
  const cls =
    tone === "emerald"
      ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-100"
      : tone === "sky"
      ? "border-sky-400/20 bg-sky-400/10 text-sky-100"
      : tone === "amber"
      ? "border-amber-300/25 bg-amber-300/10 text-amber-100"
      : "border-white/12 bg-white/6 text-white/80";

  return (
    <span className={["inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide", cls].join(" ")}>
      {children}
    </span>
  );
};

const TopBar = ({ onHome = () => {} }) => (
  <div className="flex items-center justify-between px-6 py-5 mt-20">
    <button onClick={onHome} className="group inline-flex items-center gap-2" title="InterviewGenieAI Chat">
      <div className="h-9 w-9 rounded-2xl bg-white/8 border border-white/12 backdrop-blur-xl flex items-center justify-center">
        <Sparkles className="h-4.5 w-4.5 text-white/85" />
      </div>
      <div className="leading-tight text-left">
        <div className="text-sm font-semibold tracking-tight">Chat Interview Suite</div>
        <div className="text-[11px] text-white/55 -mt-0.5">InterviewGenieAI</div>
      </div>
    </button>

    <div className="hidden md:flex items-center gap-2">
      <Pill tone="emerald">Live</Pill>
      <Pill tone="sky">AI Feedback</Pill>
      <Pill tone="amber">Final Report</Pill>
    </div>

    <div className="flex items-center gap-2">
      <PrimaryButton className="hidden sm:inline-flex" onClick={() => {}}>Practice Mode</PrimaryButton>
    </div>
  </div>
);


const getStoredFinalConfiguration = () => {
  try {
    const raw = localStorage.getItem("finalInterviewConfiguration");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const normalizeInterviewType = (value) => {
  const v = String(value || "").toLowerCase().replace(/\s+/g, "_");
  if (["behavioral", "technical", "stress", "panel"].includes(v)) return v;
  if (v === "case_study" || v === "case-study" || v === "case") return "behavioral";
  return "behavioral";
};

const normalizeCompanyId = (company) => {
  const raw =
    typeof company === "string"
      ? company
      : company?.id || company?.name || company?.label || company?.value || "";

  const key = String(raw).toLowerCase().trim().replace(/\s+/g, "-");
  const preset = COMPANY_PRESETS.find((c) => c.id === key || c.name.toLowerCase() === key);
  return preset?.id || key;
};

const buildChatConfigFromFinal = (finalConfig = {}, fallback = {}) => {
  const selectedCompanies = Array.isArray(finalConfig?.selectedCompanies)
    ? finalConfig.selectedCompanies.map(normalizeCompanyId).filter(Boolean)
    : [];

  return {
    targetRole:
      finalConfig?.selectedRole ||
      finalConfig?.jobRole ||
      fallback?.jobRole ||
      "Software Engineer",
    industry:
      finalConfig?.selectedIndustry ||
      finalConfig?.industry ||
      finalConfig?.selectedCompanyType ||
      "General",
    companies: selectedCompanies.length ? selectedCompanies : ["google", "meta"],
    interviewType: normalizeInterviewType(finalConfig?.interviewType || finalConfig?.selectedType),
    difficulty:
      finalConfig?.personality?.difficulty ||
      finalConfig?.difficulty ||
      fallback?.difficulty ||
      "intermediate",
    language:
      finalConfig?.personality?.accent ||
      finalConfig?.language ||
      fallback?.language ||
      "en-US",
    durationMinutes: Number(finalConfig?.duration) || 15,
    sessionConfig: fallback?.sessionConfig || finalConfig?.sessionConfig || finalConfig,
  };
};

function getQuestionText(value) {
  if (value && typeof value === "object") return String(value.question || value.text || "").trim();
  return String(value || "").trim();
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function formatTime(sec) {
  const s = Math.max(0, sec);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function wordCount(text) {
  const t = (text || "").trim();
  if (!t) return 0;
  return t.split(/\s+/).length;
}

function detectFillerCount(text) {
  const fillers = ["um", "uh", "like", "you know", "i mean", "so", "well", "okay"];
  const lower = (text || "").toLowerCase();
  let count = 0;
  for (const f of fillers) {
    const re = new RegExp(`\\b${f.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
    const matches = lower.match(re);
    count += matches ? matches.length : 0;
  }
  return count;
}

function contentCompletenessScore(wordTotal = 0, starScore = 0) {
  const wc = Number(wordTotal) || 0;
  const star = Number(starScore) || 0;

  if (wc <= 0) return 0;
  if (wc < 10) return clamp(18 + wc * 4 + star * 4, 0, 55);
  if (wc < 35) return clamp(45 + (wc - 10) * 1.1 + star * 5, 0, 78);
  if (wc < 70) return clamp(68 + (wc - 35) * 0.45 + star * 4, 0, 88);
  return clamp(82 + star * 4, 0, 100);
}

function detectStar(text) {
  const lower = (text || "").toLowerCase();
  const hasSituation = /situation|context|background/.test(lower);
  const hasTask = /task|goal|responsible|objective/.test(lower);
  const hasAction = /action|did|implemented|built|led|managed|designed/.test(lower);
  const hasResult = /result|impact|outcome|improved|reduced|increased|delivered/.test(lower);
  const score = [hasSituation, hasTask, hasAction, hasResult].filter(Boolean).length; // 0..4
  return {
    score,
    label:
      score >= 3 ? "STAR detected" : score === 2 ? "Partial STAR" : score === 1 ? "Weak structure" : "No structure",
  };
}

function insightToText(item, fallback = "") {
  if (!item) return fallback;
  if (typeof item === "string") return item;
  return (
    item.action ||
    item.issue ||
    item.description ||
    item.detail ||
    item.what_to_improve ||
    item.example_improvement ||
    fallback ||
    ""
  );
}

function recommendationToText(rec) {
  if (!rec) return "";
  if (typeof rec === "string") return rec;

  const action = rec.action || rec.issue || rec.description || "";
  const firstStep = Array.isArray(rec.specific_steps) ? rec.specific_steps.find(Boolean) : "";

  if (action && firstStep && firstStep !== action) return `${action}: ${firstStep}`;
  return action || firstStep || rec.detail || "";
}

function isFillerOnlyAdvice(text = "") {
  return /\bfiller|fillers|micro-pauses|pauses instead of filler/i.test(String(text));
}

function getWeakestFeedbackCategory(metrics = {}) {
  const candidates = [
    { key: "clarity", category: "Clarity", value: metrics.clarity },
    { key: "professionalism", category: "Professionalism", value: metrics.professionalism },
    { key: "confidence", category: "Confidence", value: metrics.confidence },
    { key: "pacing", category: "Pacing", value: metrics.pacing },
    { key: "depth", category: "Answer Depth", value: metrics.depth },
    { key: "relevance", category: "Content", value: metrics.relevance },
  ]
    .filter((x) => typeof x.value === "number")
    .sort((a, b) => a.value - b.value);

  return candidates[0] || null;
}

function buildQuestionSpecificCoachHint({ answer, question, metrics, resp }) {
  const wc = wordCount(answer);
  const fillerCount = Number(metrics?.fillerWords || 0);
  const aiValid = resp?.ai_analysis && !resp?.ai_analysis?.used_fallback;

  // Short answers need depth, not filler advice.
  if (wc < 10) {
    return "Your answer is too short. Add a direct reason, one concrete example, and the result or lesson learned.";
  }

  if (wc < 35) {
    return "Good start, but add depth: give context, explain what you personally did, then close with the outcome.";
  }

  const aiFeedback = typeof resp?.ai_analysis?.detailed_feedback === "string"
    ? resp.ai_analysis.detailed_feedback.trim()
    : "";

  if (aiValid && aiFeedback && !(fillerCount === 0 && isFillerOnlyAdvice(aiFeedback))) {
    const firstSentence = aiFeedback.split(/(?<=[.!?])\s+/)[0];
    return firstSentence.length > 12 ? firstSentence : aiFeedback;
  }

  const recs = Array.isArray(resp?.local_analysis?.insights?.recommendations)
    ? resp.local_analysis.insights.recommendations
    : [];

  const weakest = getWeakestFeedbackCategory(metrics);
  const usableRecs = recs.filter((rec) => {
    const text = recommendationToText(rec);
    if (!text) return false;
    if (fillerCount === 0 && isFillerOnlyAdvice(text)) return false;
    return true;
  });

  const categoryMatch = weakest
    ? usableRecs.find((rec) => String(rec?.category || "").toLowerCase() === weakest.category.toLowerCase())
    : null;

  const selected = categoryMatch || usableRecs[0];
  const selectedText = recommendationToText(selected);
  if (selectedText) return selectedText;

  if ((metrics?.star?.score ?? 0) <= 2) {
    return "Use STAR: quick context, your responsibility, what you did, and the measurable outcome.";
  }

  if (fillerCount > 2) {
    return "Reduce filler words by replacing them with a short pause, then continue with your next point.";
  }

  return "Strong direction. Make the answer more convincing with one measurable result or specific example.";
}

function buildImprovementList({ answer, metrics, resp }) {
  const wc = wordCount(answer);
  const fillerCount = Number(metrics?.fillerWords || 0);
  const areaItems = Array.isArray(resp?.local_analysis?.insights?.areas_for_improvement)
    ? resp.local_analysis.insights.areas_for_improvement.map((x) => insightToText(x)).filter(Boolean)
    : [];
  const recItems = Array.isArray(resp?.local_analysis?.insights?.recommendations)
    ? resp.local_analysis.insights.recommendations.map(recommendationToText).filter(Boolean)
    : [];
  const aiItems = Array.isArray(resp?.ai_analysis?.specific_improvements)
    ? resp.ai_analysis.specific_improvements.map((x) => insightToText(x)).filter(Boolean)
    : [];

  const merged = [...areaItems, ...recItems, ...aiItems].filter((text) => {
    if (!text) return false;
    if (fillerCount === 0 && isFillerOnlyAdvice(text)) return false;
    return true;
  });

  if (wc < 10) merged.unshift("Add a complete answer: direct reason → example → outcome.");
  else if (wc < 35) merged.unshift("Add more detail: context, your action, and the result.");

  if ((metrics?.star?.score ?? 0) <= 2) merged.push("Use STAR: situation → task → action → result.");
  if (fillerCount >= 3) merged.push("Reduce filler words and tighten phrasing.");

  return [...new Set(merged)].slice(0, 4);
}

function scoreHeuristic(answer, typeId) {
  // Subtle + stable “live metrics” heuristic.
  const wc = wordCount(answer);
  const filler = detectFillerCount(answer);
  const star = detectStar(answer).score;

  // Base heuristics
  const relevance = clamp(65 + Math.min(25, wc / 6) - filler * 3, 0, 100);
  const clarity = clamp(62 + star * 8 + Math.min(18, wc / 10) - filler * 2, 0, 100);
  const confidence = clamp(60 + Math.min(25, wc / 8) - filler * 4, 0, 100);

  // Type adjustments (very light)
  const adj =
    typeId === "stress"
      ? { confidence: -3, clarity: +1 }
      : typeId === "technical"
      ? { clarity: +3, relevance: +2 }
      : typeId === "panel"
      ? { relevance: +2 }
      : { relevance: 0 };

  const out = {
    relevance: clamp(relevance + (adj.relevance || 0), 0, 100),
    clarity: clamp(clarity + (adj.clarity || 0), 0, 100),
    confidence: clamp(confidence + (adj.confidence || 0), 0, 100),
    fillerWords: filler,
    star: detectStar(answer),
  };

  return out;
}

function pickConfidenceFromAnalysis(resp) {
  const aiTone = resp?.ai_analysis?.validated_metrics?.tone;
  const localTone = resp?.local_analysis?.metrics?.tone;
  const tone = typeof aiTone === "number" ? aiTone : typeof localTone === "number" ? localTone : 0;
  return clamp(Math.round(tone), 0, 100);
}

function mapBackendMetricsToChat(resp, fallbackMetrics, typeId) {
  /**
   * Backend /analyze response shape:
   *
   * resp.local_analysis.metrics           → { clarity, pacing, professionalism, tone, response_time, word_count, filler_count, wpm }
   * resp.ai_analysis.validated_metrics    → { clarity, pacing, professionalism, tone, response_time }  (AI-validated)
   * resp.ai_analysis.content_quality      → { relevance, depth, structure, examples_used }             (AI content scores)
   * resp.ai_analysis.star_method_analysis → { used, situation, task, action, result }                  (mapped from star_method)
   *
   * Frontend field mapping:
   *   clarity        → validated_metrics.clarity  OR local_metrics.clarity
   *   relevance      → content_quality.relevance  (real relevance from AI) OR pacing (local fallback)
   *   confidence     → validated_metrics.tone     OR local_metrics.tone
   *   professionalism→ validated_metrics.professionalism OR local_metrics.professionalism
   *   pacing         → validated_metrics.pacing   OR local_metrics.pacing  (speaking speed)
   *   depth          → content_quality.depth      (AI only)
   *   structure      → content_quality.structure  (AI only, 0-100)
   */

  const lm = resp?.local_analysis?.metrics || {};
  const aiValid = resp?.ai_analysis && !resp?.ai_analysis?.used_fallback;
  const vm = aiValid ? (resp.ai_analysis?.validated_metrics || {}) : {};
  const cq = aiValid ? (resp.ai_analysis?.content_quality   || {}) : {};

  // clarity
  const clarity = typeof vm.clarity === "number" ? vm.clarity
    : typeof lm.clarity === "number" ? lm.clarity
    : fallbackMetrics?.clarity;

  // relevance: AI content_quality.relevance is the true relevance score (0-100).
  // If AI did not run, do NOT use pacing as relevance; use answer completeness instead.
  const localWordCount = typeof lm.word_count === "number"
    ? lm.word_count
    : typeof resp?.local_analysis?.word_count === "number"
    ? resp.local_analysis.word_count
    : null;
  const completenessFallback = contentCompletenessScore(localWordCount ?? 0, fallbackMetrics?.star?.score ?? 0);
  const relevance = typeof cq.relevance === "number" ? cq.relevance
    : completenessFallback || fallbackMetrics?.relevance;

  // confidence: "tone" field = assertiveness/confidence
  const confidence = typeof vm.tone === "number" ? vm.tone
    : typeof lm.tone === "number" ? lm.tone
    : fallbackMetrics?.confidence;

  // professionalism
  const professionalism = typeof vm.professionalism === "number" ? vm.professionalism
    : typeof lm.professionalism === "number" ? lm.professionalism
    : null;

  // pacing (speaking speed, distinct from relevance)
  const pacing = typeof vm.pacing === "number" ? vm.pacing
    : typeof lm.pacing === "number" ? lm.pacing
    : null;

  // response_time
  const responseTime = typeof vm.response_time === "number" ? vm.response_time
    : typeof lm.response_time === "number" ? lm.response_time
    : null;

  // depth & structure: AI content_quality when available, completeness fallback otherwise.
  const depth      = typeof cq.depth     === "number" ? cq.depth     : (localWordCount !== null ? completenessFallback : null);
  const structure  = typeof cq.structure === "number" ? cq.structure : null;
  const examplesUsed = typeof cq.examples_used === "boolean" ? cq.examples_used : null;

  // filler words: local_analysis is most accurate (actual word scan)
  const filler =
    typeof resp?.local_analysis?.total_filler_words === "number"
      ? resp.local_analysis.total_filler_words
      : typeof lm.filler_count === "number"
      ? lm.filler_count
      : fallbackMetrics?.fillerWords ?? 0;

  // STAR: ai_analysis.star_method_analysis (backend maps star_method → star_method_analysis)
  const starFromAI = resp?.ai_analysis?.star_method_analysis;
  let star = fallbackMetrics?.star;
  if (starFromAI && typeof starFromAI === "object") {
    const s = [starFromAI.situation, starFromAI.task, starFromAI.action, starFromAI.result].filter(Boolean).length;
    star = {
      score: clamp(starFromAI.used ? s : 0, 0, 4),
      label: starFromAI.used
        ? (s >= 3 ? "STAR detected" : s >= 2 ? "Partial STAR" : "Weak structure")
        : "No structure",
      detail: starFromAI, // raw flags for sidebar STAR breakdown
    };
  }

  return {
    ...fallbackMetrics,
    // Core display metrics
    clarity:         clamp(Math.round(clarity       ?? 0), 0, 100),
    relevance:       clamp(Math.round(relevance     ?? 0), 0, 100),
    confidence:      clamp(Math.round(confidence    ?? 0), 0, 100),
    professionalism: professionalism !== null
      ? clamp(Math.round(professionalism), 0, 100)
      : (fallbackMetrics?.professionalism ?? null),
    // Extra backend-only fields (null when not available)
    pacing:       pacing       !== null ? clamp(Math.round(pacing),       0, 100) : null,
    responseTime: responseTime !== null ? clamp(Math.round(responseTime), 0, 100) : null,
    depth:        depth        !== null ? clamp(Math.round(depth),        0, 100) : null,
    structure:    structure    !== null ? clamp(Math.round(structure),    0, 100) : null,
    examplesUsed,
    fillerWords:  clamp(Math.round(filler ?? 0), 0, 999),
    star,
    // AI overall score when valid
    aiScore: aiValid && typeof resp.ai_analysis?.ai_score === "number"
      ? clamp(Math.round(resp.ai_analysis.ai_score), 0, 100)
      : null,
  };
}


function getErrMsg(e) {
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    "Something went wrong. Please try again."
  );
}

const GlowBackdrop = () => null;

const HairlineGrid = () => null;

function glassPanelClass(extra = "") {
  return [
    "rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]",
    extra,
  ].join(" ");
}

function softGlow() {
  return (
    <>
      <div className="pointer-events-none absolute -top-20 -right-20 h-52 w-52 rounded-full bg-emerald-400/10 blur-[60px]" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 h-56 w-56 rounded-full bg-sky-400/10 blur-[65px]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_420px_at_60%_0%,rgba(255,255,255,0.08),transparent_62%)]" />
    </>
  );
}

const PremiumChip = ({ icon: Icon, children, tone = "neutral" }) => {
  const map = {
    neutral: "bg-white/5 border-white/10 text-white/75",
    purple: "bg-sky-400/10 border-sky-400/20 text-sky-100",
    green: "bg-emerald-500/10 border-emerald-500/20 text-emerald-200",
    amber: "bg-amber-500/10 border-amber-500/20 text-amber-200",
    red: "bg-red-500/10 border-red-500/20 text-red-200",
  };

  return (
    <span className={["inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold tracking-wide", map[tone] || map.neutral].join(" ")}>
      {Icon ? <Icon className="h-4 w-4 opacity-90" /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
};

export default function ChatInterviewTest() {
  const location = useLocation();
  // Steps: config wizard -> interview -> results
  const [stage, setStage] = useState("config"); // "config" | "interview" | "results"
  const [wizardStep, setWizardStep] = useState(1);
  
  const cfg = useMemo(() => loadConfiguration(), []);
  const routeConfiguration = location?.state?.configuration || location?.state?.finalInterviewConfiguration || null;
  const savedFinalConfiguration = useMemo(() => routeConfiguration || getStoredFinalConfiguration(), [routeConfiguration]);
  const [shouldAutoStartConfigured] = useState(() => {
    try {
      return Boolean(location?.state?.fromSetup || localStorage.getItem("chatInterviewAutoStart") === "1");
    } catch {
      return Boolean(location?.state?.fromSetup);
    }
  });
  const mappedFinalConfiguration = useMemo(
    () => buildChatConfigFromFinal(savedFinalConfiguration || {}, cfg || {}),
    [savedFinalConfiguration, cfg]
  );
  const existingInterviewId = useMemo(() => {
    try {
      return localStorage.getItem("current_interview_id") || location?.state?.interviewId || null;
    } catch {
      return location?.state?.interviewId || null;
    }
  }, [location?.state?.interviewId]);
  const sessionConfig = mappedFinalConfiguration?.sessionConfig || cfg?.sessionConfig;
  const configuredJobRole = mappedFinalConfiguration?.targetRole || cfg?.jobRole || "";
  const configuredDifficulty = mappedFinalConfiguration?.difficulty || cfg?.difficulty || "intermediate";
  const configuredLanguage = mappedFinalConfiguration?.language || cfg?.language || "en-US";
  const configuredTotalQuestions = useMemo(() => {
    const requested =
      Number(savedFinalConfiguration?.estimatedQuestions) ||
      Number(savedFinalConfiguration?.questionCount) ||
      Number(savedFinalConfiguration?.questionsCount) ||
      TOTAL_QUESTIONS;
    return clamp(Math.round(requested), 1, 20);
  }, [savedFinalConfiguration]);
  const configuredDurationSec = useMemo(() => {
    const minutes = Number(savedFinalConfiguration?.duration) || 15;
    return clamp(Math.round(minutes * 60), 5 * 60, 180 * 60);
  }, [savedFinalConfiguration]);

  const [showConfetti,setShowConfetti]=useState(false);

  const hydratedDefaultsRef = useRef(false);
  useEffect(() => {
    if (hydratedDefaultsRef.current) return;
    // Hydrate chat defaults from the final setup configuration once.
    if (mappedFinalConfiguration?.targetRole) setTargetRole(mappedFinalConfiguration.targetRole);
    if (mappedFinalConfiguration?.industry) setIndustry(mappedFinalConfiguration.industry);
    if (mappedFinalConfiguration?.companies?.length) setCompanies(mappedFinalConfiguration.companies);
    if (mappedFinalConfiguration?.interviewType) setInterviewType(mappedFinalConfiguration.interviewType);
    hydratedDefaultsRef.current = true;
  }, [mappedFinalConfiguration]);

  // Config state
  const [targetRole, setTargetRole] = useState(mappedFinalConfiguration?.targetRole || "Frontend Developer");
  const [industry, setIndustry] = useState(mappedFinalConfiguration?.industry || "SaaS / AI");
  const [companies, setCompanies] = useState(mappedFinalConfiguration?.companies?.length ? mappedFinalConfiguration.companies : ["google", "meta"]);
  const [interviewType, setInterviewType] = useState(mappedFinalConfiguration?.interviewType || "behavioral"); // one of INTERVIEW_TYPES ids


  // Backend-driven flow (mirrors PhoneInterviewTest)
  const [questions, setQuestions] = useState(() => (FALLBACK_QUESTIONS || []).slice(0, configuredTotalQuestions));
  const [startBusy, setStartBusy] = useState(false);
  const [startError, setStartError] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [finalRes, setFinalRes] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [lastBackendAnalysis, setLastBackendAnalysis] = useState(null);
  // Tracks the backend-resolved metrics for the most recently submitted answer
  const [lastSubmittedMetrics, setLastSubmittedMetrics] = useState(null);

  const perQuestionRef = useRef([]);
  const finalizeOnceRef = useRef(false);
  const questionShownAtRef = useRef(null);
  const answerStartTimeRef = useRef(null);
  const delaySecondsRef = useRef(0);

  // Interview state
  const [timeLeft, setTimeLeft] = useState(configuredDurationSec);
  const [qIndex, setQIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [messages, setMessages] = useState(() => [
    {
      id: "sys-1",
      role: "ai",
      speaker: "Interviewer",
      content:
        "Welcome. This is a structured AI interview. Answer clearly, focus on impact, and keep it professional.\nWhen you're ready, we’ll begin with Question 1.",
      meta: { kind: "intro" },
      ts: Date.now(),
    },
  ]);

  // Per-question analytics (stored after each submit)
  const [perQuestion, setPerQuestion] = useState([]); // {q, a, metrics, score, analysis}
  const [endedByUser, setEndedByUser] = useState(false);

  useEffect(() => {
    perQuestionRef.current = perQuestion;
  }, [perQuestion]);

  // Typing / confidence detection (lightweight)
  const startedTypingAtRef = useRef(null);
  const lastEditAtRef = useRef(null);
  const editCountRef = useRef(0);

  // Live metrics (do not overwhelm: subtle + calm)
  const live = useMemo(() => scoreHeuristic(answer, interviewType), [answer, interviewType]);

  const progressPct = useMemo(() => {
    const done = Math.min(configuredTotalQuestions, qIndex);
    return Math.round((done / configuredTotalQuestions) * 100);
  }, [qIndex]);

  const question = useMemo(() => getQuestionText((questions && questions[qIndex]) || null), [questions, qIndex]);

  // Start timer only when in interview stage
  useEffect(() => {
    if (stage !== "interview") return;

    const t = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(t);
  }, [stage]);

  // Auto-end when timer hits 0
  useEffect(() => {
    if (stage !== "interview") return;
    if (timeLeft > 0) return;
    endInterview(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, stage]);

  // When entering interview stage, push first question (once questions are ready)
  useEffect(() => {
    if (stage !== "interview") return;
    if (!questions || !questions.length) return;

    const hasPrompt = messages.some((m) => m.meta?.kind === "question" && m.meta?.qIndex === 0);
    if (!hasPrompt) pushAIQuestion(0);
  }, [stage, questions, messages]);

  function toggleCompany(id) {
    setCompanies((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function nextWizard() {
    setWizardStep((s) => clamp(s + 1, 1, 3));
  }
  function prevWizard() {
    setWizardStep((s) => clamp(s - 1, 1, 3));
  }

  async function startInterview(options = {}) {
    setStartError("");
    setStartBusy(true);

    try {
      const useExistingSession = Boolean(options?.useExistingSession && existingInterviewId);
      // Reset finalization flags for a fresh run
      finalizeOnceRef.current = false;
      setFinalRes(null);
      setFinalizing(false);

      // 1) Gatekeeping/session creation.
      // When the user came from InterviewSetupConfiguration, the interview slot is already reserved
      // and the backend session is already created. Reusing it prevents double usage deduction.
      if (!useExistingSession) {
        try {
          const gate = await checkIfCanUseInterview();

          // Support different backend shapes
          const canUse =
            typeof gate === "boolean"
              ? gate
              : gate?.canUse ?? gate?.can_use ?? gate?.allowed ?? gate?.ok ?? true;

          if (!canUse) {
            const msg = gate?.message || gate?.reason || "You can’t start a new interview right now.";
            throw new Error(msg);
          }
        } catch (e) {
          // If the service throws a structured error, surface it.
          throw e;
        }

        try {
          const payload = {
            selectedFormat: "chat",
            duration: configuredDurationSec,
            interviewType,
            selectedType: interviewType,
            selectedRole: targetRole || configuredJobRole || "Software Engineer",
            selectedIndustry: industry,
            selectedCompanies: companies,
            difficulty: configuredDifficulty,
            accent: configuredLanguage,
            sessionConfig,
          };

          // Some implementations expect (role, config, difficulty) — try object first, then fallback.
          let created = null;
          try {
            created = await createInterviewSession(payload);
          } catch (err) {
            created = await createInterviewSession(
              payload.selectedRole,
              sessionConfig,
              configuredDifficulty,
              interviewType
            );
          }

          const interviewId =
            created?.interviewId ||
            created?.interview_id ||
            created?.id ||
            created?.sessionId ||
            created?.session_id ||
            created?.interview?.id ||
            null;

          if (interviewId) localStorage.setItem("current_interview_id", String(interviewId));
        } catch {
          // Session creation is best-effort — interview can still run locally.
        }
      }

      // 2) Generate questions (backend, with fallback)
      let qs = null;
      try {
        const role = targetRole || configuredJobRole || "Software Engineer";
        qs = await generateQuestions(role, sessionConfig, configuredDifficulty);
      } catch {
        qs = null;
      }

      if (!Array.isArray(qs) || !qs.length) {
        qs = (FALLBACK_QUESTIONS || []).slice(0, configuredTotalQuestions);
      }
      qs = qs.slice(0, configuredTotalQuestions).map(getQuestionText).filter(Boolean);

      // Reset interview state
      setQuestions(qs);
      setTimeLeft(configuredDurationSec);
      setQIndex(0);
      setAnswer("");
      setPerQuestion([]);
      perQuestionRef.current = [];
      setEndedByUser(false);
      setLastBackendAnalysis(null);
      setLastSubmittedMetrics(null);

      setMessages([
        {
          id: "sys-1",
          role: "ai",
          speaker: "Interviewer",
          content:
            "Welcome. This is a structured AI interview. Answer clearly, focus on impact, and keep it professional.",
          meta: { kind: "intro" },
          ts: Date.now(),
        },
      ]);

      setStage("interview");
    } catch (e) {
      setStartError(getErrMsg(e));
    } finally {
      setStartBusy(false);
    }
  }

  const autoStartRef = useRef(false);
  useEffect(() => {
    if (autoStartRef.current) return;
    if (stage !== "config") return;
    if (!shouldAutoStartConfigured) return;
    if (savedFinalConfiguration?.selectedFormat && savedFinalConfiguration.selectedFormat !== "chat") return;

    autoStartRef.current = true;
    try {
      localStorage.removeItem("chatInterviewAutoStart");
    } catch {
      // ignore storage failures
    }
    startInterview({ useExistingSession: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, shouldAutoStartConfigured, savedFinalConfiguration?.selectedFormat]);

  async function endInterview(userTriggered = true, pqOverride = null) {
    if (stage !== "interview") return;
    setEndedByUser(userTriggered);
    setStage("results");
    await finalizeInterview(pqOverride || perQuestionRef.current);
  }

  function pushAIQuestion(index) {
    const q = (questions && questions[index]) || "";
    if (!q) return;

    const ts = Date.now();
    // Reset timing refs for the new question (mirrors PhoneInterviewTest semantics)
    questionShownAtRef.current = ts;
    answerStartTimeRef.current = null;
    delaySecondsRef.current = 0;
    startedTypingAtRef.current = null;
    lastEditAtRef.current = null;
    editCountRef.current = 0;

    setMessages((prev) => [
      ...prev,
      {
        id: `q-${index}-${ts}`,
        role: "ai",
        speaker:
          interviewType === "panel"
            ? PANEL_PERSONAS[index % PANEL_PERSONAS.length].name
            : "Interviewer",
        content: q,
        meta: { kind: "question", qIndex: index, est: "60–90s" },
        ts,
      },
    ]);
  }

  function pushUserAnswer(text) {
    setMessages((prev) => [
      ...prev,
      {
        id: `u-${Date.now()}`,
        role: "user",
        speaker: "You",
        content: text,
        meta: { kind: "answer", qIndex },
        ts: Date.now(),
      },
    ]);
  }

  function computeFinalScore(pq) {
    if (!pq.length) return 0;
    const avg = pq.reduce((acc, x) => acc + x.score, 0) / pq.length;
    return Math.round(avg);
  }

  const finalScore = useMemo(() => {
    // Prefer backend: ai_analysis.final_score > local_analysis.final_score > computed
    const s =
      typeof finalRes?.ai_analysis?.final_score === "number"
        ? finalRes.ai_analysis.final_score
        : typeof finalRes?.final_score === "number"
        ? finalRes.final_score
        : typeof finalRes?.local_analysis?.final_score === "number"
        ? finalRes.local_analysis.final_score
        : null;

    return typeof s === "number" ? clamp(Math.round(s), 0, 100) : computeFinalScore(perQuestion);
  }, [finalRes, perQuestion]);

  const scoreLabel = useMemo(() => {
    const s = finalScore;
    if (s >= 85) return { label: "Excellent Candidate", icon: Crown, tone: "bg-emerald-500/15 text-emerald-200" };
    if (s >= 70) return { label: "Strong Candidate", icon: CheckCircle2, tone: "bg-sky-400/10 text-sky-100" };
    if (s >= 55) return { label: "Developing", icon: AlertTriangle, tone: "bg-amber-500/15 text-amber-200" };
    return { label: "Needs Improvement", icon: AlertTriangle, tone: "bg-rose-500/15 text-rose-200" };
  }, [finalScore]);

  const radarData = useMemo(() => {
    // If final backend result has aggregated metrics, use them directly
    const aggM = finalRes?.local_analysis?.statistics?.average_metrics;
    if (aggM && typeof aggM.clarity === "number") {
      return [
        { subject: "Communication", A: clamp(Math.round(aggM.clarity), 0, 100) },
        { subject: "Problem Solving", A: clamp(Math.round(aggM.pacing), 0, 100) },
        { subject: "Confidence", A: clamp(Math.round(aggM.tone), 0, 100) },
        { subject: "Structure", A: clamp(Math.round((finalRes?.local_analysis?.statistics?.star_method_usage_count ?? 0) * 25), 0, 100) },
        { subject: "Professionalism", A: clamp(Math.round(aggM.professionalism), 0, 100) },
      ];
    }

    // Build from per-question metrics (or live if empty)
    const base = perQuestion.length
      ? perQuestion.reduce(
          (acc, x) => {
            acc.communication += x.metrics.clarity;
            acc.problemSolving += x.metrics.relevance;
            acc.confidence += x.metrics.confidence;
            acc.structure += (x.metrics.star?.score ?? 0) * 25; // 0..100
            acc.depth += x.metrics.professionalism != null ? x.metrics.professionalism : (interviewType === "technical" ? x.metrics.relevance : x.metrics.clarity);
            return acc;
          },
          { communication: 0, problemSolving: 0, confidence: 0, structure: 0, depth: 0 }
        )
      : {
          communication: live.clarity,
          problemSolving: live.relevance,
          confidence: live.confidence,
          structure: live.star.score * 25,
          depth: interviewType === "technical" ? live.relevance : live.clarity,
        };

    const n = Math.max(1, perQuestion.length);
    const norm = (v) => Math.round(v / n);

    return [
      { subject: "Communication", A: clamp(norm(base.communication), 0, 100) },
      { subject: "Problem Solving", A: clamp(norm(base.problemSolving), 0, 100) },
      { subject: "Confidence", A: clamp(norm(base.confidence), 0, 100) },
      { subject: "Structure", A: clamp(norm(base.structure), 0, 100) },
      { subject: "Depth", A: clamp(norm(base.depth), 0, 100) },
    ];
  }, [finalRes, perQuestion, live, interviewType]);

  function handleAnswerChange(v) {
    setAnswer(v);
    const now = Date.now();

    if (!startedTypingAtRef.current) {
      startedTypingAtRef.current = now;
      answerStartTimeRef.current = now;

      const shownAt = questionShownAtRef.current || now;
      delaySecondsRef.current = Math.max(0, Math.round(((now - shownAt) / 1000) * 10) / 10);
    }

    if (!lastEditAtRef.current) lastEditAtRef.current = now;

    // count edits only when user meaningfully changes
    editCountRef.current += 1;
    lastEditAtRef.current = now;
  }

  function buildAnswerMeta() {
    const now = Date.now();
    const started = startedTypingAtRef.current || now;
    const typingSec = Math.max(1, Math.round((now - started) / 1000));
    const edits = editCountRef.current || 0;
    const delaySec = delaySecondsRef.current || 0;

    // reset refs for next question
    startedTypingAtRef.current = null;
    lastEditAtRef.current = null;
    editCountRef.current = 0;
    delaySecondsRef.current = 0;
    answerStartTimeRef.current = null;

    return { typingSec, edits, delaySec };
  }

  async function submitAnswer() {
    const a = answer.trim();
    if (!a) return;
    if (sendBusy) return;

    setSendBusy(true);

    try {
      const curQ = question || "";

      // Ensure timing refs are set (in case user pasted instantly)
      if (!answerStartTimeRef.current) {
        const now = Date.now();
        answerStartTimeRef.current = now;
        if (!questionShownAtRef.current) questionShownAtRef.current = now;
        delaySecondsRef.current = Math.max(0, Math.round(((now - questionShownAtRef.current) / 1000) * 10) / 10);
      }

      // Push user message immediately for snappy UX
      pushUserAnswer(a);
      setAnswer("");

      // Heuristic baseline (used as fallback / UI live metrics)
      const heuristicMetrics = scoreHeuristic(a, interviewType);

      // Backend analysis (source of truth when available)
      let resp = null;
      try {
        resp = await analyzeTextWithBackend(a, curQ, answerStartTimeRef, delaySecondsRef.current || 0);
        setLastBackendAnalysis(resp || null);
      } catch {
        resp = null;
      }

      // Capture meta after backend call (buildAnswerMeta resets refs)
      const meta = buildAnswerMeta();

      const metrics = resp ? mapBackendMetricsToChat(resp, heuristicMetrics, interviewType) : heuristicMetrics;

      // Store backend metrics for sidebar display
      setLastSubmittedMetrics({ metrics, resp });

      // Score: prefer backend final_score, else blend
      const score = typeof resp?.final_score === "number"
        ? clamp(Math.round(resp.final_score), 0, 100)
        : clamp(Math.round(
            0.36 * (metrics.relevance ?? 0) +
            0.36 * (metrics.clarity ?? 0) +
            0.28 * (metrics.confidence ?? 0) -
            Math.min(10, metrics.fillerWords ?? 0) * 0.8
          ), 0, 100);

      // Prefer backend insights when available, but filter generic filler-only advice when no fillers exist.
      const backendStrengths = (resp?.local_analysis?.insights?.strengths || [])
        .map((s) => insightToText(s))
        .filter(Boolean);
      const realImprovements = buildImprovementList({ answer: a, metrics, resp });
      const coachHint = buildQuestionSpecificCoachHint({ answer: a, question: curQ, metrics, resp });

      const item = {
        qIndex,
        q: curQ,
        a,
        metrics,
        meta,
        score,
        analysis: resp || null,
        strengths: backendStrengths.length ? backendStrengths : [
          metrics.clarity >= 75 ? "Clear and well-structured explanation." : null,
          metrics.relevance >= 75 ? "Stayed on-topic with relevant details." : null,
          metrics.confidence >= 75 ? "Confident tone and direct wording." : null,
          (metrics.star?.score ?? 0) >= 3 ? "Strong STAR-style structure." : null,
        ].filter(Boolean),
        improvements: realImprovements.length ? realImprovements : [
          (metrics.fillerWords ?? 0) >= 3 ? "Reduce filler words and tighten phrasing." : null,
          (metrics.star?.score ?? 0) <= 2 ? "Use STAR: situation → task → action → result." : null,
          wordCount(a) < 70 ? "Add one concrete example + measurable impact." : null,
          wordCount(a) > 220 ? "Trim to key points; avoid over-explaining." : null,
        ].filter(Boolean),
        suggested:
          coachHint ||
          "Start with a one-line summary, add 2–3 key highlights, then close with measurable impact. Keep it crisp and specific.",
      };

      const nextPQ = [...(perQuestionRef.current || []), item];
      perQuestionRef.current = nextPQ;
      setPerQuestion(nextPQ);

      // Coach micro-hint: question-specific, not always the first backend recommendation.
      const coach = coachHint;

      setMessages((prev) => [
        ...prev,
        {
          id: `hint-${Date.now()}`,
          role: "ai",
          speaker: "Coach",
          content: typeof coach === "string" ? coach : "Good. Keep it structured and measurable.",
          meta: { kind: "hint", qIndex },
          ts: Date.now(),
        },
      ]);

      // Move to next question or end
      if (qIndex + 1 >= configuredTotalQuestions || qIndex + 1 >= (questions?.length || 0)) {
        await endInterview(false, nextPQ);
        return;
      }

      const next = qIndex + 1;
      setQIndex(next);
      setTimeout(() => pushAIQuestion(next), 250);
    } finally {
      setSendBusy(false);
    }
  }

  async function skipQuestion() {
    if (sendBusy) return;

    const curQ = question || "";

    // Skipping should feel controlled, not gamified.
    setMessages((prev) => [
      ...prev,
      {
        id: `u-skip-${Date.now()}`,
        role: "user",
        speaker: "You",
        content: "I’d like to skip this question.",
        meta: { kind: "skip", qIndex },
        ts: Date.now(),
      },
      {
        id: `ai-skip-${Date.now()}`,
        role: "ai",
        speaker: "Interviewer",
        content: "Understood. Moving to the next question.",
        meta: { kind: "skipAck", qIndex },
        ts: Date.now(),
      },
    ]);

    // Reset answer + refs
    setAnswer("");
    startedTypingAtRef.current = null;
    lastEditAtRef.current = null;
    editCountRef.current = 0;
    delaySecondsRef.current = 0;
    answerStartTimeRef.current = null;

    const item = {
      qIndex,
      q: curQ,
      a: "",
      metrics: scoreHeuristic("", interviewType),
      meta: { typingSec: 0, edits: 0, delaySec: 0 },
      score: 0,
      strengths: [],
      improvements: ["Skipped. Try to answer even briefly to build consistency."],
      suggested: "Even a short structured response is better than skipping.",
      analysis: null,
    };

    const nextPQ = [...(perQuestionRef.current || []), item];
    perQuestionRef.current = nextPQ;
    setPerQuestion(nextPQ);

    if (qIndex + 1 >= configuredTotalQuestions || qIndex + 1 >= (questions?.length || 0)) {
      await endInterview(false, nextPQ);
      return;
    }

    const next = qIndex + 1;
    setQIndex(next);
    setTimeout(() => pushAIQuestion(next), 200);
  }


  async function finalizeInterview(pqList = []) {
    if (finalizeOnceRef.current) return;
    finalizeOnceRef.current = true;

    setFinalizing(true);

    try {
      const list = Array.isArray(pqList) ? pqList : [];
      const answered = list.filter((x) => x && x.q);

      if (!answered.length) {
        setFinalRes(null);
        return;
      }

      // Aggregate filler words from backend analysis when available
      const fillerWordsAgg = answered.reduce((acc, x) => {
        const fw =
          x?.analysis?.local_analysis?.filler_words ||
          x?.analysis?.local_analysis?.fillerWords ||
          x?.analysis?.local_analysis?.filler_words_map ||
          null;

        if (fw && typeof fw === "object" && !Array.isArray(fw)) {
          Object.entries(fw).forEach(([k, v]) => {
            acc[k] = (acc[k] || 0) + (Number(v) || 0);
          });
        } else {
          acc._total = (acc._total || 0) + (Number(x?.metrics?.fillerWords) || 0);
        }
        return acc;
      }, {});

      const avg = (key) => {
        if (!answered.length) return 0;
        const sum = answered.reduce((acc, x) => acc + (Number(x?.metrics?.[key]) || 0), 0);
        return clamp(Math.round(sum / answered.length), 0, 100);
      };

      const answersForAI = answered.map((x) => ({
        question: x.q,
        transcript: x.a || "",
        duration: Number(x?.meta?.typingSec) || 0,
        delay: Number(x?.meta?.delaySec ?? x?.analysis?.duration) || 2,
        metrics: {
          // Backend expects: clarity, pacing, response_time, professionalism, tone
          clarity: Number(x?.metrics?.clarity) || 0,
          pacing: Number(x?.metrics?.pacing ?? x?.analysis?.local_analysis?.metrics?.pacing ?? x?.metrics?.relevance) || 0,
          confidence: Number(x?.metrics?.confidence) || 0,     // kept for reference
          tone: Number(x?.metrics?.confidence ?? x?.analysis?.local_analysis?.metrics?.tone) || 0,
          response_time: Number(x?.analysis?.local_analysis?.metrics?.response_time ?? x?.metrics?.responseTime) || 0,
          professionalism: Number(x?.analysis?.local_analysis?.metrics?.professionalism ?? x?.metrics?.professionalism) || 0,
        },
      }));

      const summary = {
        clarity: avg("clarity"),
        pacing: avg("relevance"),
        confidence: avg("confidence"),
        relevance: avg("relevance"),
      };

      const final = await analyzeAnswersWithAI(answersForAI, fillerWordsAgg, summary);
      setFinalRes(final || null);

      const interviewId = localStorage.getItem("current_interview_id") || null;
      await submitInterviewResultsPrepared({
        interviewId,
        finalRes: final,
        answersList: answersForAI,
        fillerWords: fillerWordsAgg,
        mode: "chat",
      });
      
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
      }, 6000);
    } catch {
      // Keep UI usable even if backend fails
      setFinalRes(null);
    } finally {
      setFinalizing(false);
    }
  }

  const headerType = useMemo(() => INTERVIEW_TYPES.find((t) => t.id === interviewType), [interviewType]);

  return (
    <AppBackground>
      <div className="min-h-screen w-full relative  pb-12 text-white">  
      <GlowBackdrop />
      <HairlineGrid />
      
      {/* TechApp shell handles the hero inside the config stage. */}

        {/* Background: luxury dark + subtle cosmic vibe (matches reference style) */}
        {/* <div className="pointer-events-none fixed inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(120,119,198,0.14),transparent_55%),radial-gradient(ellipse_at_center,rgba(236,72,153,0.08),transparent_55%),radial-gradient(ellipse_at_bottom,rgba(59,130,246,0.08),transparent_60%)]" />
          <div className="absolute inset-0 opacity-[0.35] [background-image:radial-gradient(rgba(255,255,255,0.10)_1px,transparent_1px)] [background-size:22px_22px]" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/60 to-black/80" />
        </div> */}
  
        {/* Main */}
        <div className="relative z-10 mx-auto max-w-[1500px] px-4 sm:px-6 lg:px-8 pb-10 pt-6">
          {showConfetti && <Confetti width={1500} height={800} />}  
          {stage === "config" && (
            <>
              <TopBar />
              <div className="mx-auto max-w-7xl px-2 pb-10 pt-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  <div className="lg:col-span-5">
                    <div className="space-y-5">
                      <Pill tone="sky">
                        <Sparkles className="h-3.5 w-3.5" />
                        Chat interview-grade signal
                      </Pill>

                      <h1 className="text-[40px] leading-[1.05] sm:text-[52px] font-semibold tracking-tight">
                        Practice chat interviews
                        <span className="text-white/60"> with real pressure & clarity.</span>
                      </h1>

                      <p className="text-white/65 text-base sm:text-lg leading-relaxed max-w-xl">
                        Configure the role, interview type, and company flavor — then answer in a focused split-screen workspace with live scoring and a final report.
                      </p>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <PrimaryButton onClick={() => setWizardStep(3)}>
                          Review Setup
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </PrimaryButton>
                        <GhostButton onClick={() => startInterview()} disabled={startBusy}>
                          {startBusy ? "Preparing..." : "Start Chat Session"}
                        </GhostButton>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-2">
                        <Pill tone="emerald">Real-time feedback</Pill>
                        <Pill tone="sky">Structured answers</Pill>
                        <Pill>Company style</Pill>
                        <Pill tone="amber">Report radar</Pill>
                      </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { icon: Target, title: "Role Target", value: targetRole || "Not set" },
                        { icon: MessageSquare, title: "Questions", value: `${configuredTotalQuestions}` },
                        { icon: Clock, title: "Duration", value: `${Math.round(configuredDurationSec / 60)} min` },
                        { icon: ShieldCheck, title: "Mode", value: "Chat Simulation" },
                      ].map((item, idx) => (
                        <Glass key={idx} className="p-4">
                          <div className="flex items-center gap-2 text-xs text-white/60">
                            <item.icon className="h-4 w-4" />
                            {item.title}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-white/90 truncate">{item.value}</div>
                        </Glass>
                      ))}
                    </div>

                    <div className="mt-4">
                      <Glass className="p-5">
                        <div className="text-sm text-white/70">
                          Tip: answer with context → action → measurable impact. Keep it specific and clear.
                        </div>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          <Pill tone="emerald">Clarity</Pill>
                          <Pill tone="sky">Relevance</Pill>
                          <Pill>Confidence</Pill>
                        </div>
                      </Glass>
                    </div>
                  </div>

                  <div className="lg:col-span-7">
                    <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-4">
                      <Glass className="p-6 relative overflow-hidden min-h-[520px]">
                        {softGlow()}
                        <div className="relative z-10 flex h-full flex-col">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <div className="text-lg font-semibold tracking-tight">Configure Chat Interview</div>
                              <div className="text-sm text-white/60 mt-1">Same premium flow as the technical suite.</div>
                            </div>
                            <Pill tone="sky">Step {wizardStep}/3</Pill>
                          </div>

                          <div className="mt-5 h-[2px] w-full bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-white/50" style={{ width: `${(wizardStep / 3) * 100}%` }} />
                          </div>

                          <div className="mt-6 flex-1 space-y-6">
                            {wizardStep === 1 && (
                              <div className="space-y-5">
                                <div className="flex items-center gap-2 text-sm font-semibold text-white/85">
                                  <Target className="h-4 w-4 text-sky-200" />
                                  Role Target
                                </div>

                                <div className="grid gap-4 sm:grid-cols-2">
                                  <div className="space-y-2">
                                    <Label className="text-white/70">Target Role</Label>
                                    <Input
                                      value={targetRole}
                                      onChange={(e) => setTargetRole(e.target.value)}
                                      className="rounded-2xl bg-white/[0.04] border-white/10 text-white placeholder:text-white/40"
                                      placeholder="e.g., Frontend Developer"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label className="text-white/70">Industry</Label>
                                    <Input
                                      value={industry}
                                      onChange={(e) => setIndustry(e.target.value)}
                                      className="rounded-2xl bg-white/[0.04] border-white/10 text-white placeholder:text-white/40"
                                      placeholder="e.g., FinTech, SaaS, Healthcare"
                                    />
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <Label className="text-white/70">Inspired Companies</Label>
                                  <div className="flex flex-wrap gap-2">
                                    {COMPANY_PRESETS.map((c) => {
                                      const active = companies.includes(c.id);
                                      return (
                                        <button
                                          key={c.id}
                                          onClick={() => toggleCompany(c.id)}
                                          className={[
                                            "group inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm transition",
                                            active
                                              ? "border-white/20 bg-white/[0.10] text-white"
                                              : "border-white/10 bg-white/[0.04] text-white/70 hover:bg-white/[0.07]",
                                          ].join(" ")}
                                        >
                                          <Building2 className="h-4 w-4 text-white/60 group-hover:text-white/80" />
                                          {c.name}
                                          {active && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-sky-300/80" />}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <div className="text-xs text-white/50">This makes the simulation feel company-grade.</div>
                                </div>
                              </div>
                            )}

                            {wizardStep === 2 && (
                              <div className="space-y-5">
                                <div className="flex items-center gap-2 text-sm font-semibold text-white/85">
                                  <Stars className="h-4 w-4 text-sky-200" />
                                  Interview Type
                                </div>

                                <div className="grid gap-3 sm:grid-cols-2">
                                  {INTERVIEW_TYPES.map((t) => {
                                    const active = interviewType === t.id;
                                    const Icon = t.icon;
                                    return (
                                      <button
                                        key={t.id}
                                        onClick={() => setInterviewType(t.id)}
                                        className={[
                                          "text-left rounded-3xl border p-4 transition relative overflow-hidden",
                                          active ? "border-white/20 bg-white/[0.10]" : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]",
                                        ].join(" ")}
                                      >
                                        <div className="flex items-start gap-3">
                                          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/5 border border-white/10">
                                            <Icon className="h-5 w-5 text-white/80" />
                                          </div>
                                          <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                              <div className="text-sm font-semibold">{t.title}</div>
                                              {active && <Pill tone="sky">Selected</Pill>}
                                            </div>
                                            <div className="mt-1 text-xs text-white/60">{t.desc}</div>
                                            <div className="mt-3 text-[11px] text-white/45">{t.tone}</div>
                                          </div>
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {wizardStep === 3 && (
                              <div className="space-y-5">
                                <div className="flex items-center gap-2 text-sm font-semibold text-white/85">
                                  <ShieldCheck className="h-4 w-4 text-emerald-200" />
                                  Ready to Start
                                </div>

                                <div className="grid gap-3 sm:grid-cols-2">
                                  {[
                                    { icon: UserRound, title: "Target", value: targetRole || "Frontend Developer" },
                                    { icon: Building2, title: "Industry", value: industry || "General" },
                                    { icon: MessageSquare, title: "Type", value: headerType?.title || "Behavioral" },
                                    { icon: Clock, title: "Duration", value: `${Math.round(configuredDurationSec / 60)} min` },
                                  ].map((x, idx) => (
                                    <div key={idx} className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                                      <div className="flex items-center gap-2 text-xs text-white/60">
                                        <x.icon className="h-4 w-4" />
                                        {x.title}
                                      </div>
                                      <div className="mt-1 text-sm font-semibold text-white/90 truncate">{x.value}</div>
                                    </div>
                                  ))}
                                </div>

                                {startError ? (
                                  <div className="rounded-3xl border border-rose-500/20 bg-rose-500/10 p-4 text-sm text-rose-100">
                                    <div className="flex items-start gap-2">
                                      <AlertTriangle className="mt-0.5 h-4 w-4 text-rose-200/90" />
                                      <div className="min-w-0">
                                        <div className="font-semibold">Can’t start interview</div>
                                        <div className="mt-1 text-rose-100/80">{startError}</div>
                                      </div>
                                    </div>
                                  </div>
                                ) : null}

                                <PrimaryButton onClick={() => startInterview()} disabled={startBusy} className="w-full py-4 text-base">
                                  {startBusy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Sparkles className="mr-2 h-5 w-5" />}
                                  {startBusy ? "Preparing interview..." : "Start AI Interview"}
                                  <ArrowRight className="ml-2 h-5 w-5" />
                                </PrimaryButton>
                              </div>
                            )}
                          </div>

                          <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-4">
                            <GhostButton onClick={prevWizard} disabled={wizardStep === 1}>
                              <ChevronLeft className="mr-2 h-4 w-4" />
                              Back
                            </GhostButton>
                            <GhostButton onClick={nextWizard} disabled={wizardStep === 3}>
                              Next
                              <ChevronRight className="ml-2 h-4 w-4" />
                            </GhostButton>
                          </div>
                        </div>
                      </Glass>

                      <div className="space-y-4">
                        {INTERVIEW_TYPES.slice(0, 3).map((t) => {
                          const Icon = t.icon;
                          const active = interviewType === t.id;
                          return (
                            <button key={t.id} onClick={() => { setInterviewType(t.id); setWizardStep(2); }} className="text-left group w-full">
                              <Glass className="p-5 h-full relative overflow-hidden hover:bg-white/[0.06] transition">
                                <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-sky-400/10 blur-[55px]" />
                                <div className="relative flex items-start justify-between gap-4">
                                  <div className="flex gap-3">
                                    <div className="h-12 w-12 rounded-2xl border border-white/12 bg-white/6 flex items-center justify-center">
                                      <Icon className="h-5 w-5 text-sky-200" />
                                    </div>
                                    <div>
                                      <div className="text-lg font-semibold tracking-tight">{t.title}</div>
                                      <div className="text-sm text-white/60 mt-1">{t.tone}</div>
                                    </div>
                                  </div>
                                  <div className="opacity-60 group-hover:opacity-100 transition">
                                    {active ? <CheckCircle2 className="h-5 w-5 text-emerald-200" /> : <ArrowRight className="h-5 w-5" />}
                                  </div>
                                </div>
                                <div className="relative mt-4 text-sm text-white/70">{t.desc}</div>
                              </Glass>
                            </button>
                          );
                        })}

                        <Glass className="p-5">
                          <div className="text-sm font-semibold text-white/85">Real-time Feedback</div>
                          <div className="mt-4 space-y-3">
                            {[
                              { label: "Clarity", value: 92 },
                              { label: "Relevance", value: 88 },
                              { label: "Confidence", value: 86 },
                            ].map((m) => (
                              <div key={m.label}>
                                <div className="flex items-center justify-between text-xs text-white/60">
                                  <span>{m.label}</span>
                                  <span>{(m.value / 10).toFixed(1)}/10</span>
                                </div>
                                <div className="mt-2 h-[2px] w-full bg-white/10 rounded-full overflow-hidden">
                                  <div className="h-full bg-white/50" style={{ width: `${m.value}%` }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </Glass>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {stage === "interview" && (
            <div className="-mx-4 -my-6 flex min-h-screen overflow-hidden">
              {/* Left Rail — TechApp workspace style */}
              <div className="w-[76px] md:w-[92px] px-3 py-6 flex flex-col items-center gap-4">
                <Glass className="w-full p-3 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-white/85" />
                </Glass>

                <div className="w-full">
                  <Glass className="p-2">
                    <div className="h-[2px] w-full bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-white/50" style={{ width: `${progressPct}%` }} />
                    </div>
                    <div className="mt-2 text-[10px] text-white/55 text-center tracking-widest uppercase">
                      ACTIVE • {formatTime(timeLeft)}
                    </div>
                  </Glass>
                </div>

                <div className="w-full">
                  <Glass className="p-2 text-center">
                    <div className="text-[10px] text-white/45 tracking-widest uppercase">Question</div>
                    <div className="mt-1 text-sm font-semibold text-white/85">
                      {Math.min(qIndex + 1, configuredTotalQuestions)}/{configuredTotalQuestions}
                    </div>
                  </Glass>
                </div>

                <div className="flex-1" />

                <button onClick={() => endInterview(true)} className="w-full" title="End Interview" disabled={sendBusy || finalizing}>
                  <Glass className="p-3 hover:bg-white/[0.06] transition">
                    <div className="flex items-center justify-center">
                      <div className="h-9 w-9 rounded-2xl border border-rose-300/20 bg-rose-300/10 flex items-center justify-center">
                        <div className="h-3 w-3 rounded-md bg-rose-200/90" />
                      </div>
                    </div>
                  </Glass>
                </button>
              </div>

              {/* Main Workspace */}
              <div className="flex-1 flex flex-col lg:flex-row min-w-0 mt-20">
                {/* Chat Panel */}
                <div className="flex-1 min-w-0 p-4">
                  <Glass className="h-full min-h-[calc(100vh-120px)] overflow-hidden flex flex-col">
                    <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-b from-white/[0.06] to-transparent flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-2xl border border-white/12 bg-white/6 flex items-center justify-center">
                          <Sparkles className="h-5 w-5 text-sky-200" />
                        </div>
                        <div className="leading-tight">
                          <div className="text-sm font-semibold tracking-tight text-white/90">{targetRole}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <Pill tone="sky">{headerType?.title}</Pill>
                            <Pill tone="emerald">{industry}</Pill>
                            <Pill>Chat Mode</Pill>
                          </div>
                        </div>
                      </div>

                      <GhostButton onClick={() => endInterview(true)} disabled={sendBusy || finalizing} className="hidden sm:inline-flex">
                        <X className="mr-2 h-4 w-4" />
                        End Interview
                      </GhostButton>
                    </div>

                    <div className="px-5 pt-4">
                      <div className="h-[2px] w-full bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-white/50" style={{ width: `${progressPct}%` }} />
                      </div>
                    </div>

                    <div className="flex-1 overflow-auto px-5 py-5 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.18)_transparent]">
                      <div className="space-y-3">
                        {messages.map((m) => {
                          const isUser = m.role === "user";
                          const isCoach = m.speaker === "Coach";
                          const bubble = isUser
                            ? "bg-white/[0.08] border-white/12 shadow-[0_10px_35px_rgba(0,0,0,0.28)]"
                            : isCoach
                            ? "bg-sky-400/10 border-sky-300/20"
                            : "bg-white/[0.04] border-white/10";
                          const align = isUser ? "justify-end" : "justify-start";
                          const maxW = isUser ? "max-w-[82%]" : "max-w-[86%]";
                          const titleTone = isCoach ? "text-sky-100" : "text-white/80";

                          return (
                            <div key={m.id} className={`flex ${align}`}>
                              <div className={`${maxW} rounded-3xl border ${bubble} p-4`}>
                                <div className="flex items-center justify-between gap-3">
                                  <div className={`text-xs font-semibold ${titleTone}`}>
                                    {m.speaker}
                                    {m.meta?.kind === "question" && (
                                      <span className="ml-2 text-white/45 font-medium">
                                        • Question {m.meta.qIndex + 1} • Est. {m.meta.est}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-white/35">
                                    {new Date(m.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                  </div>
                                </div>
                                <div className="mt-2 whitespace-pre-line text-sm leading-relaxed text-white/80">{m.content}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border-t border-white/10 p-5 bg-gradient-to-t from-black/20 to-transparent">
                      <div className="flex items-center justify-between gap-3 text-xs text-white/55">
                        <span>Answer professionally. Keep it structured. End with impact.</span>
                        <span>Words: <span className="text-white/80 font-semibold">{wordCount(answer)}</span></span>
                      </div>

                      <textarea
                        value={answer}
                        onChange={(e) => handleAnswerChange(e.target.value)}
                        placeholder="Type your answer…"
                        className="mt-3 min-h-[124px] w-full resize-none rounded-3xl border border-white/10 bg-white/[0.04] p-4 text-sm leading-6 text-white/85 outline-none placeholder:text-white/35 focus:border-white/20"
                      />

                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs text-white/55">
                          <Mic className="h-4 w-4" />
                          Chat interview mode — clarity & structure matter.
                        </div>

                        <div className="flex items-center gap-2">
                          <GhostButton onClick={skipQuestion} disabled={sendBusy}>
                            <SkipForward className="mr-2 h-4 w-4" />
                            Skip
                          </GhostButton>
                          <PrimaryButton onClick={submitAnswer} disabled={sendBusy || !answer.trim()}>
                            {sendBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                            {sendBusy ? "Analyzing..." : "Send"}
                          </PrimaryButton>
                        </div>
                      </div>
                    </div>
                  </Glass>
                </div>

                {/* Feedback Panel */}
                <div className="w-full lg:w-[420px] xl:w-[480px] p-4 lg:pl-0">
                  <Glass className="h-full min-h-[calc(100vh-120px)] p-5 lg:p-6 overflow-hidden relative">
                    {softGlow()}
                    <div className="relative z-10">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-lg font-semibold tracking-tight">AI Interview Feedback</div>
                          <div className="text-sm text-white/60">
                            {lastSubmittedMetrics ? "Last answer analyzed." : "Live metrics — calm, subtle, helpful."}
                          </div>
                        </div>
                        <Pill tone={lastSubmittedMetrics ? "emerald" : "sky"}>{lastSubmittedMetrics ? "Backend" : "Live"}</Pill>
                      </div>

                      <div className="my-5 h-px bg-white/10" />

                      {(() => {
                        const bm = lastSubmittedMetrics?.metrics;
                        const br = lastSubmittedMetrics?.resp;
                        const aiValid = br?.ai_analysis && !br?.ai_analysis?.used_fallback;

                        const displayMetrics = bm ? [
                          {
                            label: "Clarity",
                            value: bm.clarity,
                            hint: br?.local_analysis?.insights?.score_breakdown?.clarity?.grade
                              ? `Grade: ${br.local_analysis.insights.score_breakdown.clarity.grade} · Filler + lexical quality`
                              : "Structure + understandable phrasing",
                          },
                          {
                            label: "Relevance",
                            value: bm.relevance,
                            hint: aiValid ? "AI-scored: how well the answer addressed the question" : "Estimated from speaking pace",
                          },
                          {
                            label: "Confidence",
                            value: bm.confidence,
                            hint: br?.local_analysis?.insights?.score_breakdown?.tone?.grade
                              ? `Grade: ${br.local_analysis.insights.score_breakdown.tone.grade} · Assertiveness`
                              : "Assertive and direct delivery",
                          },
                          ...(bm.professionalism != null ? [{ label: "Professionalism", value: bm.professionalism, hint: "Professional tone and vocabulary" }] : []),
                          ...(bm.pacing != null ? [{ label: "Pacing", value: bm.pacing, hint: br?.local_analysis?.metrics?.wpm ? `${br.local_analysis.metrics.wpm} WPM · ideal 130–150` : "Speaking speed and rhythm" }] : []),
                          ...(bm.depth != null ? [{ label: "Depth", value: bm.depth, hint: "AI-scored: specificity and detail level" }] : []),
                        ] : [
                          { label: "Clarity", value: live.clarity, hint: "Structure + understandable phrasing" },
                          { label: "Relevance", value: live.relevance, hint: "Direct alignment to the question" },
                          { label: "Confidence", value: live.confidence, hint: "Direct and concise delivery" },
                        ];

                        const fillerCount = bm ? (bm.fillerWords ?? 0) : live.fillerWords;
                        const topFillers = br?.local_analysis?.insights?.statistics?.top_fillers || [];
                        const wpm = br?.local_analysis?.words_per_minute ?? br?.local_analysis?.metrics?.wpm ?? null;
                        const fillerPct = br?.local_analysis?.filler_percentage ?? null;
                        const starData = bm?.star || live.star;
                        const starDetail = bm?.star?.detail;
                        const cqStructure = bm?.structure;
                        const cqExamples = bm?.examplesUsed;
                        const responseTime = bm?.responseTime;
                        const lastCoachMsg = messages.filter(m => m.speaker === "Coach").slice(-1)[0]?.content
                          || (live.star.score >= 3
                            ? "Keep it crisp. Add one measurable result to close strong."
                            : "Use STAR: one-line context → your responsibility → key actions → measurable outcome.");

                        return (
                          <div className="space-y-3">
                            {displayMetrics.map((m) => (
                              <div key={m.label} className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                                <div className="flex items-center justify-between">
                                  <div className="text-sm font-semibold">{m.label}</div>
                                  <div className="text-sm font-semibold text-white/80">{(m.value / 10).toFixed(1)}/10</div>
                                </div>
                                <div className="mt-2 text-xs text-white/60">{m.hint}</div>
                                <div className="mt-3 h-[2px] w-full bg-white/10 rounded-full overflow-hidden">
                                  <div className="h-full bg-white/50" style={{ width: `${m.value}%` }} />
                                </div>
                              </div>
                            ))}

                            <div className="grid grid-cols-2 gap-3">
                              <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                                <div className="text-xs text-white/60">Filler words</div>
                                <div className="mt-1 text-lg font-semibold text-white/85">{fillerCount}</div>
                                {fillerPct !== null && <div className="mt-0.5 text-[10px] text-white/45">{fillerPct}% density</div>}
                                {topFillers.length > 0 && (
                                  <div className="mt-1 text-[10px] text-white/40 truncate">
                                    {topFillers.slice(0, 2).map(f => `"${f.word}" ×${f.count}`).join(" · ")}
                                  </div>
                                )}
                                <div className="mt-1 text-xs text-white/50">Aim: 0–2</div>
                              </div>

                              <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                                <div className="text-xs text-white/60">STAR structure</div>
                                <div className="mt-1 text-sm font-semibold text-white/85">{starData?.label || "—"}</div>
                                {starDetail ? (
                                  <div className="mt-2 flex gap-1">
                                    {["situation", "task", "action", "result"].map(k => (
                                      <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded-full border ${starDetail[k] ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-white/[0.03] text-white/35"}`}>
                                        {k[0].toUpperCase()}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="mt-2 text-xs text-white/50">S/T/A/R: {starData?.score ?? 0}/4</div>
                                )}
                              </div>
                            </div>

                            {(cqStructure != null || cqExamples != null || wpm !== null || responseTime !== null) && (
                              <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4 space-y-2">
                                <div className="text-sm font-semibold">Content quality</div>
                                <div className="flex flex-wrap gap-2 text-xs text-white/65">
                                  {cqStructure != null && <Pill>Structure {Math.round(cqStructure)}/100</Pill>}
                                  {cqExamples != null && <Pill tone={cqExamples ? "emerald" : "neutral"}>{cqExamples ? "Examples ✓" : "No examples"}</Pill>}
                                  {wpm !== null && <Pill tone="sky">{wpm} WPM</Pill>}
                                  {responseTime !== null && <Pill>Response {responseTime}/100</Pill>}
                                </div>
                              </div>
                            )}

                            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                              <div className="flex items-center gap-2 text-xs tracking-widest text-white/55">
                                <Sparkles className="h-4 w-4" />
                                COACH MICRO-HINT
                              </div>
                              <div className="mt-3 text-sm text-white/75">{lastCoachMsg}</div>
                            </div>

                            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-semibold">Session Focus</div>
                                <Pill>{companies.length ? `${companies.length} companies` : "No company"}</Pill>
                              </div>
                              <div className="mt-2 text-xs text-white/60">
                                Target: <span className="text-white/80 font-medium">{targetRole}</span> {"•"}{" "}
                                <span className="text-white/70">{industry}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </Glass>
                </div>
              </div>
            </div>
          )}

          {stage === "results" && (
            <div className="grid gap-6 lg:grid-cols-[1.2fr_0.85fr]">
              {/* Left: results main */}
              <div className={glassPanelClass("p-6")}>
                {softGlow()}
                <div className="relative z-10">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="text-lg font-semibold tracking-tight">Interview Results</div>
                      <div className="text-sm text-white/60">
                        {finalizing ? "Generating results…" : endedByUser ? "Ended early by user." : "Completed session."} • {targetRole} •{" "}
                        {headerType?.title}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        className="rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10"
                        onClick={() => {
                          setStage("config");
                          setWizardStep(1);
                        }}
                      >
                        Back to Setup
                      </Button>
                      <Button
                        className="rounded-2xl bg-gradient-to-r bg-white text-black hover:bg-white/90 border border-white/10"
                        onClick={startInterview}
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        Run Again
                      </Button>
                    </div>
                  </div>
  
                  <Separator className="my-5 bg-white/10" />
  
                  {/* Hero score */}
                  <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
                    <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-5">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold">Overall Score</div>
                        <Badge className={`border border-white/10 ${scoreLabel.tone}`}>
                          <scoreLabel.icon className="mr-1 h-4 w-4" />
                          {scoreLabel.label}
                        </Badge>
                      </div>
  
                      <div className="mt-4 flex items-center gap-5">
                        <div className="relative grid h-28 w-28 place-items-center rounded-full border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02]">
                          <div className="text-3xl font-semibold">{finalizing && !finalRes ? "—" : finalScore || 0}</div>
                          <div className="text-xs text-white/50">/100</div>
                          {finalizing && !finalRes ? (
                            <div className="flex items-center gap-2 text-[10px] text-white/55">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Analyzing
                            </div>
                          ) : null}
                          <div className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle_at_top,rgba(168,85,247,0.14),transparent_55%)]" />
                        </div>
  
                        <div className="min-w-0">
                          <div className="text-sm text-white/70">
                            Comparison:{" "}
                            <span className="text-white/85 font-semibold">
                              {finalScore >= 80 ? "Top 25%" : finalScore >= 70 ? "Top 35%" : "Top 55%"}
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-white/55">
                            Built from per-question performance. Detailed breakdown below.
                          </div>
                        </div>
                      </div>
  
                      <Separator className="my-5 bg-white/10" />
  
                      {/* Behavioral insights: prefer backend, fallback to derived */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm font-semibold">
                          <Brain className="h-4 w-4 text-sky-100" />
                          Behavioral Insights
                        </div>

                        <div className="space-y-2 text-sm text-white/75">
                          {(finalRes?.ai_analysis?.detailed_strengths?.length || finalRes?.local_analysis?.insights?.strengths?.length)
                            ? <>
                                {(finalRes?.ai_analysis?.detailed_strengths || finalRes?.local_analysis?.insights?.strengths || []).slice(0, 2).map((s, i) => (
                                  <InsightLine key={i} ok={true} text={typeof s === "string" ? s : s?.description || s?.detail || ""} />
                                ))}
                                {(finalRes?.ai_analysis?.detailed_weaknesses || finalRes?.local_analysis?.insights?.areas_for_improvement || []).slice(0, 1).map((s, i) => (
                                  <InsightLine key={"w" + i} ok={false} text={typeof s === "string" ? s : s?.issue || s?.detail || ""} />
                                ))}
                              </>
                            : <>
                                <InsightLine
                                  ok={finalScore >= 70}
                                  text={finalScore >= 70 ? "You maintained a professional tone and stayed mostly structured." : "Your structure varies; use STAR consistently to improve clarity."}
                                />
                                <InsightLine
                                  ok={perQuestion.some((x) => x.metrics?.fillerWords <= 2)}
                                  text={perQuestion.some((x) => x.metrics?.fillerWords >= 3) ? "Filler words appear under pressure—tighten phrasing and pause before answering." : "Good control over filler words; keep responses crisp."}
                                />
                                <InsightLine
                                  ok={perQuestion.some((x) => x.metrics?.star?.score >= 3)}
                                  text={perQuestion.some((x) => x.metrics?.star?.score >= 3) ? "Your strongest answers included clear outcomes and impact." : "Add measurable outcomes to strengthen credibility."}
                                />
                              </>
                          }
                        </div>
                      </div>
                    </div>
  
                    {/* Radar chart */}
                    <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-5">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold">Performance Radar</div>
                        <Badge className="bg-white/5 border border-white/10 text-white/70">
                          <BarChart3 className="mr-1 h-4 w-4" />
                          Analytics
                        </Badge>
                      </div>
  
                      <div className="mt-4 h-[260px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={radarData}>
                            <PolarGrid stroke="rgba(255,255,255,0.10)" />
                            <PolarAngleAxis dataKey="subject" tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 12 }} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                            <Radar dataKey="A" stroke="rgba(125,211,252,0.95)" fill="rgba(125,211,252,0.20)" />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
  
                      <div className="mt-2 text-xs text-white/55">
                        Categories are built from per-question clarity, relevance, confidence, and structure.
                      </div>
                    </div>
                  </div>
  
                  <Separator className="my-6 bg-white/10" />
  
                  {/* Question breakdown */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold">Question Breakdown</div>
                        <div className="text-xs text-white/55">
                          Expand each item for answer + strengths + improvements + suggested rewrite.
                        </div>
                      </div>
                      <Badge className="bg-white/5 border border-white/10 text-white/70">
                        {perQuestion.length}/{configuredTotalQuestions}
                      </Badge>
                    </div>
  
                    <div className="mt-4">
                      <Accordion type="single" collapsible className="w-full">
                        {(perQuestion.length ? perQuestion : []).map((x) => (
                          <AccordionItem
                            key={`pq-${x.qIndex}`}
                            value={`item-${x.qIndex}`}
                            className="rounded-3xl border border-white/10 bg-white/[0.04] px-4 mb-3"
                          >
                            <AccordionTrigger className="hover:no-underline py-4">
                              <div className="flex w-full items-center justify-between gap-4 pr-3">
                                <div className="min-w-0 text-left">
                                  <div className="text-xs text-white/50">
                                    Question {x.qIndex + 1}
                                  </div>
                                  <div className="mt-1 text-sm font-semibold text-white/85 line-clamp-1">
                                    {x.q || "—"}
                                  </div>
                                </div>
                                <Badge className="bg-white/5 border border-white/10 text-white/80">
                                  {x.score}/100
                                </Badge>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="pb-5">
                              <div className="grid gap-4 lg:grid-cols-2">
                                <div className="rounded-3xl border border-white/10 bg-black/20 p-4">
                                  <div className="text-xs font-semibold text-white/70">Your answer</div>
                                  <div className="mt-2 whitespace-pre-line text-sm text-white/75">
                                    {x.a ? x.a : <span className="text-white/45">Skipped.</span>}
                                  </div>
  
                                  <Separator className="my-4 bg-white/10" />
  
                                  <div className="grid grid-cols-3 gap-3">
                                    <MetricTile label="Clarity"    value={x.metrics?.clarity    ?? 0} />
                                    <MetricTile label="Relevance"  value={x.metrics?.relevance  ?? 0} />
                                    <MetricTile label="Confidence" value={x.metrics?.confidence ?? 0} />
                                    {x.metrics?.professionalism != null && (
                                      <MetricTile label="Professionalism" value={x.metrics.professionalism} />
                                    )}
                                    {x.metrics?.pacing != null && (
                                      <MetricTile label="Pacing" value={x.metrics.pacing} />
                                    )}
                                    {x.metrics?.depth != null && (
                                      <MetricTile label="Depth" value={x.metrics.depth} />
                                    )}
                                  </div>

                                  <div className="mt-3 grid grid-cols-2 gap-3">
                                    <SmallTile
                                      label="Filler Words"
                                      value={String(x.metrics?.fillerWords ?? 0)}
                                      sub={x.analysis?.local_analysis?.filler_percentage != null
                                        ? `${x.analysis.local_analysis.filler_percentage}% density`
                                        : undefined}
                                    />
                                    <SmallTile
                                      label="STAR"
                                      value={`${x.metrics?.star?.score ?? 0}/4`}
                                      sub={x.metrics?.star?.label}
                                    />
                                    {x.metrics?.responseTime != null && (
                                      <SmallTile label="Response time" value={`${x.metrics.responseTime}/100`} sub="delay score" />
                                    )}
                                    {x.metrics?.examplesUsed != null && (
                                      <SmallTile
                                        label="Examples"
                                        value={x.metrics.examplesUsed ? "Used ✓" : "None"}
                                        sub={x.metrics.examplesUsed ? "Concrete example present" : "Add a specific example"}
                                      />
                                    )}
                                  </div>

                                  {x.metrics?.star?.detail && (
                                    <div className="mt-3 flex items-center gap-2">
                                      <span className="text-[10px] text-white/45">STAR flags:</span>
                                      {["situation","task","action","result"].map(k => (
                                        <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded-full border ${x.metrics.star.detail[k] ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-white/[0.03] text-white/35"}`}>
                                          {k[0].toUpperCase()}
                                        </span>
                                      ))}
                                    </div>
                                  )}

                                  <div className="mt-3 text-xs text-white/55">
                                    Typing: {x.meta?.typingSec ?? 0}s • Edits: {x.meta?.edits ?? 0}
                                    {x.analysis?.local_analysis?.metrics?.wpm != null && (
                                      <span> • {x.analysis.local_analysis.metrics.wpm} WPM</span>
                                    )}
                                  </div>
                                </div>
  
                                <div className="space-y-3">
                                  <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                                    <div className="text-xs font-semibold text-white/70">Strengths</div>
                                    <ul className="mt-2 space-y-2 text-sm text-white/75">
                                      {(x.strengths?.length ? x.strengths : ["—"]).map((s, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                          <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-300/80" />
                                          <span>{s}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
  
                                  <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                                    <div className="text-xs font-semibold text-white/70">Improvements</div>
                                    <ul className="mt-2 space-y-2 text-sm text-white/75">
                                      {(x.improvements?.length ? x.improvements : ["—"]).map((s, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                          <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-300/80" />
                                          <span>{s}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
  
                                  <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                                    <div className="text-xs font-semibold text-white/70">Suggested rewrite</div>
                                    <div className="mt-2 text-sm text-white/75">{x.suggested}</div>
                                  </div>
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
  
                      {!perQuestion.length && (
                        <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.04] p-5 text-sm text-white/70">
                          No answers were recorded. Run the interview to generate a full breakdown.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
  
              {/* Right: quick actions / session summary */}
              <div className={glassPanelClass("p-6")}>
                {softGlow()}
                <div className="relative z-10">
                  <div className="text-lg font-semibold tracking-tight">Session Summary</div>
                  <div className="mt-1 text-sm text-white/60">Export and next steps (keep it serious).</div>
  
                  <Separator className="my-5 bg-white/10" />
  
                  <div className="space-y-3">
                    <SummaryRow label="Role" value={targetRole} />
                    <SummaryRow label="Industry" value={industry} />
                    <SummaryRow label="Type" value={headerType?.title || "—"} />
                    <SummaryRow label="Questions" value={`${configuredTotalQuestions}`} />
                    <SummaryRow label="Duration" value={`${Math.round(configuredDurationSec / 60)} minutes`} />
                    <SummaryRow
                      label="Companies"
                      value={
                        companies.length
                          ? companies
                              .map((id) => COMPANY_PRESETS.find((c) => c.id === id)?.name)
                              .filter(Boolean)
                              .join(", ")
                          : "—"
                      }
                    />
                  </div>
  
                  <Separator className="my-5 bg-white/10" />
  
                  <Tabs defaultValue="next" className="w-full">
                    <TabsList className="w-full bg-white/5 border border-white/10 rounded-2xl">
                      <TabsTrigger value="next" className="w-full rounded-xl">
                        Next Actions
                      </TabsTrigger>
                      <TabsTrigger value="focus" className="w-full rounded-xl">
                        Focus Plan
                      </TabsTrigger>
                    </TabsList>
  
                    <TabsContent value="next" className="mt-4 space-y-3">
                      <ActionCard
                        icon={Sparkles}
                        title="Run another session"
                        desc="Try a different interview type for realism."
                        action={
                          <Button
                            className="rounded-2xl bg-white/10 border border-white/10 hover:bg-white/15"
                            onClick={startInterview}
                          >
                            Start Again
                          </Button>
                        }
                      />
                      <ActionCard
                        icon={BarChart3}
                        title="View analytics"
                        desc="Compare sessions over time (score trend)."
                        action={
                          <Button variant="secondary" className="rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10">
                            Open Analytics
                          </Button>
                        }
                      />
                      <ActionCard
                        icon={ShieldCheck}
                        title="Prepare for a real interview"
                        desc="Schedule a timed practice routine."
                        action={
                          <Button className="rounded-2xl bg-gradient-to-r bg-white text-black hover:bg-white/90 border border-white/10">
                            Schedule
                          </Button>
                        }
                      />
                    </TabsContent>
  
                    <TabsContent value="focus" className="mt-4 space-y-3">
                      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4 text-sm text-white/75">
                        <div className="font-semibold">Your best upgrade path</div>
                        <ul className="mt-3 space-y-2">
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-300/80" />
                            <span>Open with a one-line summary for every answer.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-300/80" />
                            <span>Add one measurable outcome (%, time, scale, money).</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-300/80" />
                            <span>Use STAR consistently (S/T/A/R).</span>
                          </li>
                        </ul>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppBackground>
  );
}

/* ------------------------ Small UI helpers ------------------------ */

function MetricTile({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
      <div className="text-[11px] text-white/55">{label}</div>
      <div className="mt-1 text-sm font-semibold text-white/85">{Math.round(value)}/100</div>
      <div className="mt-2">
        <Progress value={value} className="h-2 bg-white/10" />
      </div>
    </div>
  );
}

function SmallTile({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
      <div className="text-[11px] text-white/55">{label}</div>
      <div className="mt-1 text-sm font-semibold text-white/85">{value}</div>
      {sub ? <div className="mt-1 text-[11px] text-white/50">{sub}</div> : null}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
      <div className="text-xs text-white/55">{label}</div>
      <div className="text-sm font-semibold text-white/85 text-right">{value}</div>
    </div>
  );
}

function ActionCard({ icon: Icon, title, desc, action }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-2xl bg-white/5 border border-white/10">
            <Icon className="h-5 w-5 text-white/80" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold">{title}</div>
            <div className="mt-1 text-xs text-white/60">{desc}</div>
          </div>
        </div>
        <div className="shrink-0">{action}</div>
      </div>
    </div>
  );
}

function InsightLine({ ok, text }) {
  return (
    <div className="flex items-start gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-3">
      {ok ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-300/80" />
      ) : (
        <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-300/80" />
      )}
      <div className="text-sm text-white/75">{text}</div>
    </div>
  );
}
